/**
 * Clock Panel 按钮状态纯派生 — 服务端状态(AttendanceTodayEntry + 开放
 * work session)是唯一输入,UI 不维护本地状态机、不做乐观猜测。
 * Spec: docs/superpowers/specs/2026-06-11-web-clock-panel-design.md §2
 */
import type { AttendanceTodayEntry } from "@/types/userRealtime";

export type ClockUiMode = "not_clocked_in" | "working" | "in_break" | "shift_ended";

/** 开放(endedAt=null)的 task work session 投影。 */
export interface OpenSessionInfo {
  sessionId: string;
  taskId: string;
  title: string;
  taskTypeCode: string;
  startedAt: string;
}

export interface ClockUiState {
  mode: ClockUiMode;
  canClockIn: boolean;
  canClockOut: boolean;
  /** 非 null ⇒ Clock Out 禁用并展示原因(英文)。 */
  clockOutBlockedReason: string | null;
  canStartBreak: boolean;
  canEndBreak: boolean;
  canStartWorkType: boolean;
  /** 运行中的 special session(web 可收尾)。 */
  runningSpecial: OpenSessionInfo | null;
  /** 运行中的 regular task session(web 只提示,去任务页/mobile 收尾)。 */
  runningRegular: OpenSessionInfo | null;
}

export function deriveClockUiState(
  entry: AttendanceTodayEntry | null,
  openSessions: OpenSessionInfo[],
): ClockUiState {
  const runningSpecial =
    openSessions.find((s) => s.taskTypeCode === "special") ?? null;
  const runningRegular =
    openSessions.find((s) => s.taskTypeCode !== "special") ?? null;
  const hasOpenSession = runningSpecial != null || runningRegular != null;

  const status = entry?.status ?? null;
  const mode: ClockUiMode =
    status === "working"
      ? "working"
      : status === "in_break"
        ? "in_break"
        : status === "shift_end"
          ? "shift_ended"
          : "not_clocked_in";
  const working = mode === "working";

  return {
    mode,
    // shift_end 后同日二次上班是否允许由后端裁决,UI 不做假设(spec §2)
    canClockIn: mode === "not_clocked_in" || mode === "shift_ended",
    canClockOut: working && !hasOpenSession,
    clockOutBlockedReason:
      mode === "in_break"
        ? "End your break before clocking out."
        : working && runningSpecial
          ? "End your current activity first."
          : working && runningRegular
            ? "You have a running task. Finish or pause it first."
            : null,
    canStartBreak: working && !hasOpenSession,
    canEndBreak: mode === "in_break",
    canStartWorkType: working && !hasOpenSession,
    runningSpecial,
    runningRegular,
  };
}

/** 秒 → "3h 24m" / "30m"。 */
export function formatHm(totalSec: number | null | undefined): string {
  const s = Math.max(0, Math.floor(totalSec ?? 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Break 选项(对齐 mobile BREAK_TYPE_OPTIONS,id 直接写进 start-break notes)。 */
export const BREAK_TYPE_OPTIONS = [
  { id: "lunch", label: "Lunch", emoji: "🍽️" },
  { id: "coffee", label: "Coffee", emoji: "☕" },
  { id: "rest", label: "Rest", emoji: "🧘" },
  { id: "personal", label: "Personal", emoji: "👤" },
] as const;
export type BreakTypeId = (typeof BREAK_TYPE_OPTIONS)[number]["id"];

// ────────────────────────────────────────────────
// Task 3: 格式化函数 + displayMode 派生
// 对齐 handoff core.jsx 4-13 行
// ────────────────────────────────────────────────

export const pad2 = (n: number) => String(n).padStart(2, "0");

/** 26px 大计时 / 行内计时:HH:MM:SS。 */
export function fmtHMS(s: number): string {
  return [(s / 3600) | 0, ((s % 3600) / 60) | 0, s % 60].map(pad2).join(":");
}

/** activity 胶囊:<1h → MM:SS,≥1h → HH:MM:SS(core.jsx fmtClip)。 */
export function fmtClip(s: number): string {
  return s < 3600 ? `${pad2((s / 60) | 0)}:${pad2(s % 60)}` : fmtHMS(s);
}

/** 状态展示模式(UI 态;working+开放 special = activity;loading=首拉未完成,禁止任何打卡动作)。 */
export type ClockDisplayMode = "loading" | "idle" | "working" | "activity" | "break" | "ended";

export function deriveDisplayMode(ui: ClockUiState): ClockDisplayMode {
  if (ui.mode === "working") return ui.runningSpecial ? "activity" : "working";
  if (ui.mode === "in_break") return "break";
  if (ui.mode === "shift_ended") return "ended";
  return "idle";
}

/** firstClockInAt → "9:02 AM"(core.jsx nowLabel 同格式)。 */
export function sinceLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  const m = pad2(d.getMinutes());
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ap}`;
}
